// Small, dependency-free helpers for media URLs, maps and measurements.

// Photos uploaded in admin are stored with the backend's address (…onrender.com). When the website passes
// /api/uploads/* through to the backend (a rewrite rule on Render, switched on with REACT_APP_UPLOADS_VIA_SITE=true),
// pictures are shown from the website's own domain instead, so image search credits eleganthomeadvisors.in.
// Videos and documents keep the backend address (they need byte-range streaming and stay direct).
const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const UPLOADS_VIA_SITE = String(process.env.REACT_APP_UPLOADS_VIA_SITE || "").toLowerCase() === "true";
const SITE_ORIGIN = (process.env.REACT_APP_SITE_URL || "https://eleganthomeadvisors.in").replace(/\/+$/, "");
const IMAGE_UPLOAD = /^\/api\/uploads\/[^/]+\.(webp|jpe?g|png|gif|avif)$/i;

export const publicImageUrl = (u) => {
    if (!UPLOADS_VIA_SITE || !BACKEND_ORIGIN || !u || !u.startsWith(BACKEND_ORIGIN + "/api/uploads/")) return u;
    const rest = u.slice(BACKEND_ORIGIN.length);
    if (!IMAGE_UPLOAD.test(rest.split("?")[0])) return u;
    const here = typeof window !== "undefined" ? window.location.origin : SITE_ORIGIN;
    // only on the real website (on staging or a local copy the rewrite rule doesn't exist)
    return SITE_ORIGIN && here === SITE_ORIGIN ? SITE_ORIGIN + rest : u;
};

/** Only http(s) links are ever used as href/src. Blocks javascript: and data: URLs entered by mistake. */
export const safeUrl = (u) => {
    if (!u || typeof u !== "string") return "";
    try {
        const x = new URL(u.trim(), window.location.origin);
        return x.protocol === "http:" || x.protocol === "https:" ? publicImageUrl(u.trim()) : "";
    } catch {
        return "";
    }
};

/**
 * The same picture at a sensible width for where it is shown. Uploaded photos are resized by the backend,
 * Unsplash and Pexels links get their own resize options, and any other link is returned unchanged.
 * Without this, a property card would download the full 2400-pixel photo (or a 5 MB stock original).
 */
export const sized = (u, w) => {
    const url = safeUrl(u);
    if (!url || !w) return url;
    try {
        const x = new URL(url, window.location.origin);
        if (/\/api\/uploads\/[^/]+\.webp$/.test(x.pathname)) {
            x.searchParams.set("w", String(w));
        } else if (x.hostname === "images.unsplash.com") {
            x.searchParams.set("w", String(w));
            x.searchParams.set("q", "72");
            x.searchParams.set("auto", "format");
            x.searchParams.set("fit", "crop");
        } else if (x.hostname === "images.pexels.com") {
            x.searchParams.set("auto", "compress");
            x.searchParams.set("cs", "tinysrgb");
            x.searchParams.set("w", String(w));
        } else {
            return url;
        }
        return x.toString();
    } catch {
        return url;
    }
};

export const isImageUrl = (u) => /\.(jpe?g|png|webp|avif|gif)(\?.*)?$/i.test(u || "");
export const isDirectVideo = (u) => /\.(mp4|m4v|webm|ogg|mov)(\?.*)?$/i.test(u || "");

/**
 * Google Drive video ("Anyone with the link" sharing) -> Drive's own player:
 * drive.google.com/file/d/<id>/view, /open?id=<id> or /uc?id=<id>  ->  drive.google.com/file/d/<id>/preview
 */
export const driveEmbed = (url) => {
    const clean = safeUrl(url);
    if (!clean) return null;
    try {
        const u = new URL(clean);
        if (u.hostname !== "drive.google.com" && u.hostname !== "docs.google.com") return null;
        const m = u.pathname.match(/\/file\/d\/([A-Za-z0-9_-]{10,})/);
        const id = m ? m[1] : u.searchParams.get("id");
        if (!id || !/^[A-Za-z0-9_-]{10,}$/.test(id)) return null;
        const key = u.searchParams.get("resourcekey");
        return `https://drive.google.com/file/d/${id}/preview${key && /^[A-Za-z0-9_-]+$/.test(key) ? `?resourcekey=${key}` : ""}`;
    } catch {
        return null;
    }
};

export const youtubeEmbed = (url) => {
    const clean = safeUrl(url);
    if (!clean) return null;
    try {
        const u = new URL(clean);
        const host = u.hostname.replace("www.", "");
        let id = null;
        if (host === "youtu.be") id = u.pathname.slice(1).split("/")[0];
        else if (host === "youtube.com" || host === "m.youtube.com") {
            if (u.pathname === "/watch") id = u.searchParams.get("v");
            else if (u.pathname.startsWith("/shorts/") || u.pathname.startsWith("/embed/") || u.pathname.startsWith("/live/")) id = u.pathname.split("/")[2];
        }
        return id ? `https://www.youtube.com/embed/${id}` : null;
    } catch {
        return null;
    }
};

/** Every video configured on a property, legacy fields first, without duplicates. */
export const collectVideos = (p) => {
    const list = [];
    const add = (url, title) => {
        const clean = safeUrl(url);
        if (clean && !list.some((v) => v.url === clean)) list.push({ url: clean, title: title || "" });
    };
    add(p.video_url, "Property walkthrough");
    add(p.video_url_2, "Project overview");
    (p.videos || []).forEach((v) => add(v?.url, v?.title));
    return list;
};

/** Embeddable Google Maps URL (no API key needed). Uses only data the admin entered. */
export const mapEmbedUrl = (p) => {
    const lat = Number(p.latitude), lng = Number(p.longitude);
    if (lat && lng) return `https://www.google.com/maps?q=${lat},${lng}&output=embed`;
    const raw = p.map_embed || "";
    const at = raw.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/) || raw.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
    if (at) return `https://www.google.com/maps?q=${at[1]},${at[2]}&output=embed`;
    const place = [p.name, p.address || [p.locality, p.location].filter(Boolean).join(", ")].filter(Boolean).join(", ");
    return place ? `https://www.google.com/maps?q=${encodeURIComponent(place)}&output=embed` : null;
};

export const mapLink = (p) => {
    const lat = Number(p.latitude), lng = Number(p.longitude);
    if (lat && lng) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    if (safeUrl(p.map_embed) && /google\.[a-z.]+\/maps|goo\.gl|maps\.app/.test(p.map_embed)) return p.map_embed;
    const place = [p.name, p.address || p.location].filter(Boolean).join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}`;
};

// ----- measurements -----
export const sqftToSqm = (sqft) => (sqft ? Math.round(sqft * 0.092903) : 0);
export const formatSqft = (n) => (n ? `${Math.round(n).toLocaleString("en-IN")} sq.ft` : "");

/** 19.58 ft -> 19' 7" */
export const feetInches = (ft) => {
    if (!ft) return "–";
    let feet = Math.floor(ft);
    let inches = Math.round((ft - feet) * 12);
    if (inches === 12) { feet += 1; inches = 0; }
    return `${feet}' ${inches}"`;
};
export const metres = (ft) => (ft ? `${(ft * 0.3048).toFixed(2)} m` : "–");

/** Accepts 19'7", 19' 7, 19 7, 19.6 -> decimal feet. Returns 0 when it can't be read. */
export const parseFeet = (text) => {
    const t = String(text ?? "").trim();
    if (!t) return 0;
    const ftIn = t.match(/^(\d+(?:\.\d+)?)\s*(?:'|ft|feet|\s)\s*(\d+(?:\.\d+)?)?\s*(?:"|in|inch|inches)?$/i);
    if (ftIn) return Number(ftIn[1]) + (Number(ftIn[2] || 0) / 12);
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
};

/** Best known carpet area (sq.ft) for a property, from the headline field or its floor plans. */
export const propertyCarpetArea = (p) => {
    if (p.carpet_area) return Number(p.carpet_area);
    const fromPlans = (p.floor_plans || []).map((f) => {
        if (f.carpet_area_sqft) return Number(f.carpet_area_sqft);
        const m = String(f.area || "").match(/[\d,]+(?:\.\d+)?/);
        return m ? Number(m[0].replace(/,/g, "")) : 0;
    }).filter(Boolean);
    return fromPlans.length ? Math.min(...fromPlans) : 0;
};

export const whatsappLink = (settings, text) =>
    settings?.whatsapp ? `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent(text)}` : "";

export const propertyWhatsAppText = (p) => {
    const loc = [p.locality, p.location].filter(Boolean).join(", ") || p.location || "";
    return `Hi, I am interested in ${p.name}${loc ? ` in ${loc}` : ""}. I would like to know more about pricing and schedule a Site Visit / Home Visit.`;
};

export const PLACEHOLDER_IMG =
    "data:image/svg+xml;utf8," + encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'><rect width='100%' height='100%' fill='#EEF2F6'/><text x='50%' y='50%' fill='#94A3B8' font-family='sans-serif' font-size='28' text-anchor='middle'>Image unavailable</text></svg>`
    );

// ---------- image preloading (gallery) ----------
// Remembers which picture addresses have finished downloading, so the gallery can show them at once.
const _loaded = new Set();
const _pending = new Map();

export const isPreloaded = (url) => !!url && _loaded.has(url);

/** Downloads a picture into the browser cache. Resolves when it is ready (or failed). */
export function preloadImage(url) {
    if (!url || typeof window === "undefined") return Promise.resolve();
    if (_loaded.has(url)) return Promise.resolve();
    if (_pending.has(url)) return _pending.get(url);
    const p = new Promise((resolve) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
            _loaded.add(url);
            _pending.delete(url);
            // decode ahead of time so swapping it in doesn't stutter
            (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(resolve);
        };
        img.onerror = () => { _pending.delete(url); resolve(); };
        img.src = url;
    });
    _pending.set(url, p);
    return p;
}

export const markLoaded = (url) => { if (url) _loaded.add(url); };

/** Preloads a list of pictures a few at a time, in order. Returns a function that stops it. */
export function preloadQueue(urls, concurrency = 2) {
    let stopped = false;
    const queue = urls.filter((u) => u && !_loaded.has(u));
    const worker = async () => {
        while (!stopped && queue.length) await preloadImage(queue.shift());
    };
    for (let i = 0; i < concurrency; i++) worker();
    return () => { stopped = true; };
}

/** Width of the main gallery photo for this screen: 800 / 1200 / 1600 (sizes the backend keeps ready). */
export function galleryWidth() {
    if (typeof window === "undefined") return 1600;
    const px = Math.min(window.innerWidth || 1600, 1340) * Math.min(window.devicePixelRatio || 1, 2);
    return px <= 800 ? 800 : px <= 1200 ? 1200 : 1600;
}
